/**
 * QR Connect Middleman Service
 * Handles QR connections via Octopus realUserData (Octopus-compliant)
 * 
 * This service processes QR connection requests by:
 * 1. Validating user IDs (strict type checking, format validation)
 * 2. Fetching user profiles via Octopus
 * 3. Creating connections between users
 * 
 * All operations are Octopus-compliant and go through realUserData methods.
 * 
 * HARDENED: Comprehensive validation, no side effects, predictable error responses.
 */

import { realUserData } from "./realUserData";

/**
 * Validates userId format: alphanumeric, 6-36 characters
 * Enforces: /^[A-Za-z0-9]{6,36}$/
 * @param userId - The user ID to validate
 * @returns true if valid, false otherwise
 */
function isValidUserIdFormat(userId: string): boolean {
  if (typeof userId !== 'string') {
    return false;
  }
  // Alphanumeric only, 6-36 characters - enforced regex
  const userIdRegex = /^[A-Za-z0-9]{6,36}$/;
  return userIdRegex.test(userId);
}

/**
 * Handle QR Connection Request
 * Main function to process QR connection requests
 * 
 * @param scannedUserId - The userId from the scanned QR code
 * @param currentUserId - The userId of the user who scanned the QR code
 * @returns Promise<{ success: boolean, scannedUserId?: string, error?: string }>
 * No side effects other than calling realUserData methods. Returns structured error object.
 */
export async function handleQRConnection(
  scannedUserId: string,
  currentUserId: string
): Promise<{ success: boolean; scannedUserId?: string; error?: string }> {
  const timestamp = new Date().toISOString();
  console.log(`[AUDIT] ${timestamp} QR Connection Request:`, {
    scannedUserId,
    currentUserId
  });

  // Strong input validation: type check
  if (typeof scannedUserId !== 'string') {
    const error = `Invalid scannedUserId type: expected string, got ${typeof scannedUserId}`;
    console.error(`[AUDIT] ${timestamp} Validation failed:`, error);
    return { success: false, error }; // Structured error object
  }

  // Strong input validation: type check
  if (typeof currentUserId !== 'string') {
    const error = `Invalid currentUserId type: expected string, got ${typeof currentUserId}`;
    console.error(`[AUDIT] ${timestamp} Validation failed:`, error);
    return { success: false, error }; // Structured error object
  }

  // Trim before all validations
  const trimmedScannedUserId = scannedUserId.trim();
  const trimmedCurrentUserId = currentUserId.trim();

  // Strong input validation: non-empty
  if (!trimmedScannedUserId || trimmedScannedUserId === '') {
    const error = "Missing scannedUserId";
    console.error(`[AUDIT] ${timestamp} Validation failed:`, error);
    return { success: false, error }; // Structured error object
  }

  // Strong input validation: non-empty
  if (!trimmedCurrentUserId || trimmedCurrentUserId === '') {
    const error = "Missing currentUserId";
    console.error(`[AUDIT] ${timestamp} Validation failed:`, error);
    return { success: false, error }; // Structured error object
  }

  // Strong input validation: format check (same regex)
  if (!isValidUserIdFormat(trimmedScannedUserId)) {
    const error = "scannedUserId must be alphanumeric, 6-36 characters";
    console.error(`[AUDIT] ${timestamp} Validation failed:`, { error, scannedUserId: trimmedScannedUserId });
    return { success: false, error }; // Structured error object
  }

  // Strong input validation: format check (same regex)
  if (!isValidUserIdFormat(trimmedCurrentUserId)) {
    const error = "currentUserId must be alphanumeric, 6-36 characters";
    console.error(`[AUDIT] ${timestamp} Validation failed:`, { error, currentUserId: trimmedCurrentUserId });
    return { success: false, error }; // Structured error object
  }

  // Strong input validation: reject anonymous
  if (trimmedScannedUserId === 'anonymous' || trimmedCurrentUserId === 'anonymous') {
    const error = "Cannot use anonymous as userId";
    console.error(`[AUDIT] ${timestamp} Validation failed:`, { error, scannedUserId: trimmedScannedUserId, currentUserId: trimmedCurrentUserId });
    return { success: false, error }; // Structured error object
  }

  // Strong input validation: reject self-connection
  if (trimmedScannedUserId === trimmedCurrentUserId) {
    const error = "Cannot connect user to themselves";
    console.error(`[AUDIT] ${timestamp} Validation failed:`, { error, scannedUserId: trimmedScannedUserId, currentUserId: trimmedCurrentUserId });
    return { success: false, error }; // Structured error object
  }

  try {
    // Fetch scanned user profile - only side effect: calling realUserData.getUserProfile()
    console.log(`[AUDIT] ${timestamp} Fetching scanned user profile:`, { scannedUserId: trimmedScannedUserId });
    const scanned = await realUserData.getUserProfile(trimmedScannedUserId);

    if (!scanned) {
      const error = "Scanned user not found";
      console.error(`[AUDIT] ${timestamp} Profile fetch failed:`, { scannedUserId: trimmedScannedUserId, error });
      return { success: false, error }; // Structured error object
    }

    console.log(`[AUDIT] ${timestamp} Scanned user profile fetched:`, {
      scannedUserId: trimmedScannedUserId,
      hasName: !!scanned.name,
      hasEmail: !!scanned.email
    });

    // Fetch current user profile - only side effect: calling realUserData.getUserProfile()
    console.log(`[AUDIT] ${timestamp} Fetching current user profile:`, { currentUserId: trimmedCurrentUserId });
    const current = await realUserData.getUserProfile(trimmedCurrentUserId);

    if (!current) {
      const error = "Current user not found";
      console.error(`[AUDIT] ${timestamp} Profile fetch failed:`, { currentUserId: trimmedCurrentUserId, error });
      return { success: false, error }; // Structured error object
    }

    console.log(`[AUDIT] ${timestamp} Current user profile fetched:`, {
      currentUserId: trimmedCurrentUserId,
      hasName: !!current.name,
      hasEmail: !!current.email
    });

    // Create connection - only side effect: calling realUserData.connectUsers()
    console.log(`[AUDIT] ${timestamp} Creating connection:`, {
      scannerUserId: trimmedCurrentUserId,
      scannedUserId: trimmedScannedUserId
    });
    await realUserData.connectUsers(trimmedCurrentUserId, trimmedScannedUserId);

    console.log(`[AUDIT] ${timestamp} Connection created successfully:`, {
      scannerUserId: trimmedCurrentUserId,
      scannedUserId: trimmedScannedUserId
    });

    // Return ONLY the specified shape - no HTML, no redirects
    return {
      success: true,
      scannedUserId: trimmedScannedUserId,
    };

  } catch (error) {
    // Ensure no throws escape unhandled - return structured error object instead
    const errorMessage = error instanceof Error ? error.message : String(error);
    console.error(`[AUDIT] ${timestamp} QR Connection failed:`, {
      scannedUserId: trimmedScannedUserId,
      currentUserId: trimmedCurrentUserId,
      error: errorMessage,
      stack: error instanceof Error ? error.stack : undefined
    });

    // Return structured error object - no throws escape
    return {
      success: false,
      error: errorMessage
    };
  }
}
